import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/JsonLd";

const faqData = [
  {
    q: "Ce include mentenanța?",
    a: "Mentenanța include actualizări WordPress, pluginuri și temă gestionate proactiv, monitorizare uptime, verificări malware, backup-uri, verificări funcționale de bază, suport și raportare lunară.",
  },
  {
    q: "Lucrați doar cu WordPress?",
    a: "Da. Serviciul este dedicat WordPress și WooCommerce.",
  },
  {
    q: "Faceți și WooCommerce?",
    a: "Da. Avem un plan separat pentru magazine online, cu backup-uri mai dese, verificări suplimentare și suport prioritar.",
  },
  {
    q: "Dacă site-ul meu are deja probleme?",
    a: "Începem cu un audit și îți spunem clar ce este în regulă, ce nu este și dacă site-ul poate intra direct în mentenanță sau are nevoie de remedieri inițiale.",
  },
  {
    q: "Dacă un update strică site-ul?",
    a: "Actualizările sunt gestionate controlat, nu aplicate orbește. Dacă apare o problemă, investigăm și intervenim, inclusiv prin restaurare backup dacă este necesar.",
  },
  {
    q: "Dacă nu am backup?",
    a: "Putem prelua site-ul, verifica situația actuală și configura un sistem corect de backup pentru mai multă siguranță.",
  },
  {
    q: "Lucrați pe hostingul meu actual?",
    a: "De regulă, preferăm să lucrăm în infrastructura noastră, în special atunci când proiectul are nevoie de optimizări de viteză, stabilitate și control operațional mai bun. Multe soluții de hosting, inclusiv anumite VPS-uri, au limitări de procesare care pot restrânge performanța reală a site-ului. Cel mai bine este să analizăm împreună situația și să îți spunem onest dacă infrastructura actuală este suficient de bună pentru proiect.",
  },
  {
    q: "Oferiți și infrastructură / hosting?",
    a: "Da, pentru proiectele potrivite putem oferi infrastructură managed sau soluții dedicate, configurate pentru stabilitate și predictibilitate.",
  },
  {
    q: "În cât timp răspundeți?",
    a: "Timpul de răspuns depinde de plan și de tipul solicitării. Cererile prioritare și incidentele critice sunt tratate cu prioritate, în special pentru WooCommerce și proiectele custom.",
  },
  {
    q: "Faceți și modificări pe site?",
    a: "Da, toate planurile includ modificări minore de conținut. Sunt incluse până la 30 de minute de lucru pe lună pentru ajustări simple, precum actualizare text, înlocuire imagine sau mici modificări de prezentare. Modificările mai ample se estimează și se facturează separat.",
  },
  {
    q: "Ce nu este inclus?",
    a: "Nu sunt incluse implicit redesign complet, dezvoltare custom complexă, funcționalități ample noi, copywriting complet sau marketing.",
  },
  {
    q: "Cum funcționează pentru WooCommerce?",
    a: "Planul WooCommerce include monitorizare mai atentă, verificări funcționale pentru checkout și comenzi, suport prioritar, backup fișiere zilnic, backup bază de date de 4 ori pe zi și retenție de 60 de zile.",
  },
  {
    q: "Ce se întâmplă dacă site-ul are trafic mare?",
    a: "Pentru proiectele unde traficul și stabilitatea contează mai mult, recomandăm și folosim infrastructură mai serioasă decât hostingul shared clasic, astfel încât site-ul să fie mai bine pregătit pentru cerințe reale de business.",
  },
  {
    q: "Când am nevoie de ofertă personalizată?",
    a: "Atunci când ai trafic mare, multe integrări, infrastructură specială, fluxuri complexe, cerințe avansate de suport sau un magazin online cu complexitate ridicată.",
  },
];

export default function FAQPage() {
  usePageMeta(
    "Întrebări Frecvente - Ordell WebCare",
    "Răspunsuri la cele mai frecvente întrebări despre mentenanță WordPress, WooCommerce, backup-uri, securitate și suport."
  );

  return (
    <>
      <JsonLd page="faq" data={faqData} />
      {/* Hero */}
      <section data-testid="faq-hero" className="py-16 lg:py-24">
        <div className="section-container max-w-3xl">
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
            Întrebări frecvente
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
            Răspunsuri la cele mai comune întrebări despre serviciile Ordell WebCare.
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section data-testid="faq-list" className="pb-20 lg:pb-28">
        <div className="section-container max-w-3xl">
          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white rounded-xl border border-[#0F2942]/10 px-6 data-[state=open]:shadow-sm"
              >
                <AccordionTrigger
                  data-testid={`faq-page-trigger-${i}`}
                  className="text-left text-[#1A1D20] font-medium hover:no-underline py-5"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#4A5568] leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="faq-cta" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container text-center max-w-3xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-6">
            Ai alte întrebări? Hai să discutăm.
          </h2>
          <Link
            to="/contact"
            data-testid="faq-cta-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0F2942] font-medium px-8 py-3.5 rounded-xl hover:bg-white/90 transition-all"
          >
            Cere audit gratuit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
